import { PaginationProps } from 'antd/lib/pagination';
import Row from 'antd/lib/row';
import React, { Dispatch, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IStaffDetail, IStaffSearch } from 'src/entity/DepartmentEntity';
import { IStateDepartment } from 'src/entity/StateEntity';
import { fetchDepartmentsIfNeeded } from 'src/redux/actions/departmentCreator';
import DepartmentAdminService from 'src/services/DepartmentAdminService';
import DepartmentTree from './Department/DepartmentTree';
import DepartmentUserList from './User/UserList';
import DepartmentUserSearch from './User/UserSearch';


interface IProps extends RouteComponentProps<{}, {}, {useCache: boolean}> {
  isDomestic: boolean,
  dispatch: Dispatch<IStateDepartment>
}

const DepartmentAdminListPage: React.FC<IProps> = (props) => {
  const {isDomestic, dispatch, location} = props;

  if (!isDomestic) {
    // 目前只支持境内代理商
    return (
      <Row type="flex" align="top" style={{height: '100%', flexWrap: 'nowrap'}}>
        目前只支持境内合作伙伴
      </Row>
    )
  }

  const [loading, setLoading] = useState(false);

  // 初始化用户查询参数
  const [staffQuery, setStaffQuery] = useState(() => {
    const { state } = location;
    let result: Partial<IStaffSearch> = new IStaffSearch(20);
    if (state && state.useCache) {
      // 只有从详情页面返回才使用缓存, 使用上一次查询参数
      result = DepartmentAdminService.getStaffQuery(20);
    };
    return result;
  });

  const [pagination, setPagination] = useState(() => {
    const {limit, offset} = staffQuery;
    const current = Math.floor((offset as number) / (limit as number)) + 1;
    const pageSize = limit;
    const initialPagination = DepartmentAdminService.initPagination(current, pageSize);
    return initialPagination;
  });

  const [staffList, setStaffList] = useState([] as IStaffDetail[]);
  
  const staffQueryRef = useRef<Partial<IStaffSearch>>();
  const paginationRef = useRef<PaginationProps>();

  useLayoutEffect(() => {
    staffQueryRef.current = staffQuery;
    paginationRef.current = pagination;
  })

  useEffect(() => {
    // 第一次渲染时 => 查询 [部门列表 & 用户列表]
    dispatch(fetchDepartmentsIfNeeded(true));
    doSearch(staffQuery);
  }, []);

  /** 
   * 执行查询
   */
  const doSearch = async (query: Partial<IStaffSearch>) => {
    setLoading(true);
    const res = await DepartmentAdminService.fetchDepartmentStaffs(query);
    if (res) {
      const current = Math.floor((query.offset as number) / (query.limit as number)) + 1;
      const newPagination: PaginationProps = {...pagination, current, pageSize: query.limit, total: res.count};
      setPagination(newPagination);
      setStaffList(res.results);
    }
    setLoading(false);
  };

  /**
   * 搜索按钮点击回调函数
   */
  const handleSearch = useCallback((query: Partial<IStaffSearch>) => {
    // 需要获取 pageSize
    const {pageSize} = paginationRef.current as PaginationProps;
    query.offset = 0;
    query.limit = pageSize;
    setStaffQuery(query);
    doSearch(query);
  }, []);

  /**
   * 分页器变化回调函数
   */
  const handlePaginationChange = useCallback((query:  Partial<IStaffSearch>) => {
    const param: Partial<IStaffSearch> = {...staffQueryRef.current as Partial<IStaffSearch>};
    param.limit = query.limit;
    param.offset = query.offset;
    setStaffQuery(param);
    doSearch(param);
  }, []);

  /**
   * 已选部门变化回调函数
   * @param isTop: 是否为根级部门
   */
  const handleSelectedDepartmentChanged = useCallback((selectedDepartmentId: string, isTop: boolean) => {
    const param: Partial<IStaffSearch> = {...staffQueryRef.current as Partial<IStaffSearch>};
    param.department = isTop ? undefined : selectedDepartmentId;
    param.offset = 0;
    setStaffQuery(param);
    doSearch(param);
  }, []);

  return (
    <Row type="flex" align="top" style={{height: '100%', flexWrap: 'nowrap'}}>
      <div style={{flex: '0 0 auto', width: 200, height: '100%'}}>
        <DepartmentTree
          defaultDepartmentId={staffQuery.department}
          disableSelect={loading}
          onDepartmentChange={handleSelectedDepartmentChanged} />
      </div>
      <div style={{flex: '1 1 auto', marginLeft: 30}}>
        <DepartmentUserSearch
          defaultQuery={staffQuery}
          loading={loading}
          onSearch={handleSearch}/>
        <DepartmentUserList
          defaultDepartmentId={staffQuery.department}
          pagination={pagination}
          loading={loading}
          dataSource={staffList}
          onPaginationChange={handlePaginationChange}/>
      </div>
    </Row> 
  ) 
};

export default DepartmentAdminListPage;




// WEBPACK FOOTER //
// ./src/components/views/DepartmentAdmin/DepartmentAdminListPage.tsx